import { useEffect, useLayoutEffect, useRef, useState, type ComponentType } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/cn";

/** Geometry of where the menu was opened. We treat this as the desired
 *  top-left in viewport coordinates and clamp the rendered menu inside the
 *  viewport on mount. */
export interface ContextMenuPosition {
  /** Clientx in viewport pixels (e.g. from a `MouseEvent.clientX`). */
  x: number;
  /** ClientY in viewport pixels (e.g. from a `MouseEvent.clientY`). */
  y: number;
}

export interface ContextMenuItem {
  id: string;
  label: string;
  /** Optional Lucide-style icon rendered as a small leading glyph. */
  icon?: ComponentType<{ className?: string }>;
  /** Optional secondary label rendered to the right (e.g. shortcut hint). */
  shortcut?: string;
  onSelect: () => void;
  /** Disabled items render greyed-out and don't fire `onSelect`. */
  disabled?: boolean;
  /** Insert a divider above this item (for grouping). */
  divider?: boolean;
}

interface Props {
  /** Where to anchor the menu in viewport coordinates. `null` closes the menu. */
  position: ContextMenuPosition | null;
  items: ContextMenuItem[];
  onClose: () => void;
  /** Optional aria-label for the menu container. */
  ariaLabel?: string;
}

/**
 * Lightweight floating menu rendered through a portal so it can break out of
 * any clipping ancestor (canvas wrappers, scroll containers, etc.). Closes on
 * outside click, Escape, scroll, and window resize. Position is clamped to
 * stay inside the viewport.
 */
export function ContextMenu({ position, items, onClose, ariaLabel }: Props) {
  const open = position !== null;
  const ref = useRef<HTMLDivElement | null>(null);
  // Resolved position after viewport-clamp (computed in a layout effect so the
  // first paint already lands in the right place — no flicker from a re-mount).
  const [resolved, setResolved] = useState<ContextMenuPosition | null>(null);

  useLayoutEffect(() => {
    if (!position) {
      setResolved(null);
      return;
    }
    const el = ref.current;
    if (!el) {
      setResolved(position);
      return;
    }
    const margin = 8;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let x = position.x;
    let y = position.y;
    if (x + rect.width + margin > vw) x = Math.max(margin, vw - rect.width - margin);
    if (y + rect.height + margin > vh) y = Math.max(margin, vh - rect.height - margin);
    if (x < margin) x = margin;
    if (y < margin) y = margin;
    setResolved({ x, y });
  }, [position]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    const onScroll = () => onClose();
    const onResize = () => onClose();
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (ref.current && target && ref.current.contains(target)) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    // Use capture for pointerdown so we close before a click handler under the
    // menu's would-be target steals focus.
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, onClose]);

  if (typeof document === "undefined") return null;
  if (!position) return null;

  // Render at the requested position on the very first paint, then snap to
  // the clamped position once we measured the menu. Both renders look the
  // same to the user because the menu fades in.
  const pos = resolved ?? position;

  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label={ariaLabel}
      className="fixed z-[60] min-w-[12rem] rounded-xl bg-white text-ink-900 ring-1 ring-ink-200 shadow-xl shadow-ink-900/15 py-1.5 select-none"
      style={{ left: pos.x, top: pos.y }}
      // Suppress the browser's native context menu when right-clicking inside
      // the floating menu itself (the menu lives at the document root, so it
      // wouldn't otherwise inherit the canvas's `onContextMenu` handler).
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((item) => (
        <ContextMenuRow
          key={item.id}
          item={item}
          onSelect={() => {
            if (item.disabled) return;
            item.onSelect();
            onClose();
          }}
        />
      ))}
    </div>,
    document.body
  );
}

function ContextMenuRow({
  item,
  onSelect,
}: {
  item: ContextMenuItem;
  onSelect: () => void;
}) {
  const Icon = item.icon;
  return (
    <>
      {item.divider && <div className="my-1 h-px bg-ink-100" />}
      <button
        type="button"
        role="menuitem"
        disabled={item.disabled}
        onClick={onSelect}
        className={cn(
          "flex w-full items-center gap-2.5 px-3 py-1.5 pointer-coarse:min-h-11 text-body-sm font-medium text-left transition-colors",
          item.disabled
            ? "text-ink-300 cursor-not-allowed"
            : "text-ink-800 hover:bg-ink-100 active:bg-ink-200"
        )}
      >
        {Icon ? (
          <Icon className="size-4 shrink-0 text-ink-500" />
        ) : (
          <span className="size-4 shrink-0" aria-hidden />
        )}
        <span className="flex-1">{item.label}</span>
        {item.shortcut && (
          <span className="text-caption text-ink-400 tabular-nums">
            {item.shortcut}
          </span>
        )}
      </button>
    </>
  );
}
